import {
  WorkflowDefinitionV1,
  WorkflowNodeDefinition,
  WorkflowPort,
  WorkflowTransitionDefinition,
} from '../../types/workflow-spec';

export interface WorkflowValidationIssue {
  code:
    | 'MISSING_TRIGGER'
    | 'MULTIPLE_TRIGGERS'
    | 'MISSING_END'
    | 'SELF_TRANSITION'
    | 'DUPLICATE_TRANSITION'
    | 'TRANSITION_NODE_NOT_FOUND'
    | 'TRANSITION_PORT_NOT_FOUND'
    | 'PORT_TYPE_MISMATCH'
    | 'MISSING_TRANSITION_LABEL'
    | 'UNREACHABLE_NODE'
    | 'NO_PATH_TO_END'
    | 'ROUTER_REQUIRES_BRANCHES'
    | 'TRIGGER_REQUIRES_TRANSITION'
    | 'MISSING_AGENT'
    | 'LOOPBACK_MUST_BE_EXPLICIT';
  message: string;
  details?: Record<string, unknown>;
}

export interface WorkflowValidationResult {
  valid: boolean;
  issues: WorkflowValidationIssue[];
}

const hasPort = (ports: WorkflowPort[], portId: string) => ports.some((port) => port.id === portId);
const getNodeById = (nodes: WorkflowNodeDefinition[], nodeId: string) => nodes.find((node) => node.id === nodeId);

export const validateWorkflowDefinition = (workflow: WorkflowDefinitionV1): WorkflowValidationResult => {
  const issues: WorkflowValidationIssue[] = [];
  const { nodes, transitions } = workflow;
  const adjacency = buildAdjacencyMap(nodes, transitions);

  const triggerNodes = nodes.filter((node) => node.kind === 'trigger');
  if (triggerNodes.length === 0) {
    issues.push({ code: 'MISSING_TRIGGER', message: 'Workflow must contain exactly one trigger node.' });
  }
  if (triggerNodes.length > 1) {
    issues.push({ code: 'MULTIPLE_TRIGGERS', message: 'Workflow currently supports a single trigger node.' });
  }

  const endNodes = nodes.filter((node) => node.kind === 'end');
  if (endNodes.length === 0) {
    issues.push({ code: 'MISSING_END', message: 'Workflow must contain at least one end node.' });
  }

  const seenTransitionKeys = new Set<string>();
  for (const transition of transitions) {
    if (transition.sourceNodeId === transition.targetNodeId) {
      issues.push({
        code: 'SELF_TRANSITION',
        message: 'Transitions cannot point to the same node.',
        details: { transitionId: transition.id },
      });
    }

    if (!transition.label.trim()) {
      issues.push({
        code: 'MISSING_TRANSITION_LABEL',
        message: 'Transitions must have a visible label.',
        details: { transitionId: transition.id },
      });
    }

    const key = `${transition.sourceNodeId}:${transition.sourcePortId}->${transition.targetNodeId}:${transition.targetPortId}:${transition.label}`;
    if (seenTransitionKeys.has(key)) {
      issues.push({
        code: 'DUPLICATE_TRANSITION',
        message: 'Duplicate transition detected.',
        details: { transitionId: transition.id, key },
      });
    }
    seenTransitionKeys.add(key);

    validateTransition(nodes, adjacency, transition, issues);
  }

  validateNodeSemantics(nodes, transitions, issues);
  validateReachability(nodes, adjacency, triggerNodes, issues);
  validatePathToEnd(adjacency, triggerNodes, endNodes, issues);

  return { valid: issues.length === 0, issues };
};

const validateTransition = (
  nodes: WorkflowNodeDefinition[],
  adjacency: Map<string, string[]>,
  transition: WorkflowTransitionDefinition,
  issues: WorkflowValidationIssue[]
) => {
  const sourceNode = getNodeById(nodes, transition.sourceNodeId);
  const targetNode = getNodeById(nodes, transition.targetNodeId);

  if (!sourceNode || !targetNode) {
    issues.push({
      code: 'TRANSITION_NODE_NOT_FOUND',
      message: 'Transition references a missing node.',
      details: {
        transitionId: transition.id,
        sourceNodeId: transition.sourceNodeId,
        targetNodeId: transition.targetNodeId,
      },
    });
    return;
  }

  if (!hasPort(sourceNode.outputs, transition.sourcePortId) || !hasPort(targetNode.inputs, transition.targetPortId)) {
    issues.push({
      code: 'TRANSITION_PORT_NOT_FOUND',
      message: 'Transition references a missing source or target port.',
      details: {
        transitionId: transition.id,
        sourcePortId: transition.sourcePortId,
        targetPortId: transition.targetPortId,
      },
    });
    return;
  }

  const sourcePort = sourceNode.outputs.find((port) => port.id === transition.sourcePortId);
  const targetPort = targetNode.inputs.find((port) => port.id === transition.targetPortId);
  if (sourcePort && targetPort && sourcePort.type !== targetPort.type) {
    issues.push({
      code: 'PORT_TYPE_MISMATCH',
      message: 'Source and target port types do not match.',
      details: {
        transitionId: transition.id,
        sourceType: sourcePort.type,
        targetType: targetPort.type,
      },
    });
  }

  const createsCycle = transitionCreatesCycle(transition, adjacency);
  if (createsCycle && !transition.isLoopback && !sourceNode.policy?.allowLoopback) {
    issues.push({
      code: 'LOOPBACK_MUST_BE_EXPLICIT',
      message: 'Transitions that create a cycle must be marked as loopbacks or allowed by policy.',
      details: { transitionId: transition.id, sourceNodeId: sourceNode.id, targetNodeId: targetNode.id },
    });
  }
};

const validateNodeSemantics = (
  nodes: WorkflowNodeDefinition[],
  transitions: WorkflowTransitionDefinition[],
  issues: WorkflowValidationIssue[]
) => {
  for (const node of nodes) {
    const outgoing = transitions.filter((transition) => transition.sourceNodeId === node.id);
    if (node.kind === 'trigger' && outgoing.length === 0) {
      issues.push({
        code: 'TRIGGER_REQUIRES_TRANSITION',
        message: 'Trigger node must have at least one outgoing transition.',
        details: { nodeId: node.id, nodeName: node.name },
      });
    }

    if (node.kind === 'router' && outgoing.length < 2) {
      issues.push({
        code: 'ROUTER_REQUIRES_BRANCHES',
        message: 'Router node must define at least two outgoing branches.',
        details: { nodeId: node.id, nodeName: node.name },
      });
    }

    if (
      (node.kind === 'trigger' || node.kind === 'action' || node.kind === 'conversation') &&
      !('agentId' in node && node.agentId.trim())
    ) {
      const message =
        node.kind === 'trigger'
          ? 'Trigger node must have an entry agent assigned.'
          : node.kind === 'action'
          ? 'Action node must have a functional agent assigned.'
          : 'Conversation node must have an agent assigned.';
      issues.push({
        code: 'MISSING_AGENT',
        message,
        details: { nodeId: node.id, nodeName: node.name, nodeKind: node.kind },
      });
    }
  }
};

const validateReachability = (
  nodes: WorkflowNodeDefinition[],
  adjacency: Map<string, string[]>,
  triggerNodes: WorkflowNodeDefinition[],
  issues: WorkflowValidationIssue[]
) => {
  if (triggerNodes.length === 0) return;
  const reached = new Set<string>();
  const stack = [triggerNodes[0].id];

  while (stack.length > 0) {
    const nodeId = stack.pop();
    if (!nodeId || reached.has(nodeId)) continue;
    reached.add(nodeId);
    for (const next of adjacency.get(nodeId) ?? []) stack.push(next);
  }

  for (const node of nodes) {
    if (!reached.has(node.id)) {
      issues.push({
        code: 'UNREACHABLE_NODE',
        message: 'Node is unreachable from the trigger.',
        details: { nodeId: node.id, nodeName: node.name },
      });
    }
  }
};

const validatePathToEnd = (
  adjacency: Map<string, string[]>,
  triggerNodes: WorkflowNodeDefinition[],
  endNodes: WorkflowNodeDefinition[],
  issues: WorkflowValidationIssue[]
) => {
  if (triggerNodes.length === 0 || endNodes.length === 0) return;
  const endIds = new Set(endNodes.map((node) => node.id));

  const hasPathToEnd = (startNodeId: string) => {
    const visited = new Set<string>();
    const stack = [startNodeId];

    while (stack.length > 0) {
      const nodeId = stack.pop();
      if (!nodeId || visited.has(nodeId)) continue;
      if (endIds.has(nodeId)) return true;
      visited.add(nodeId);
      for (const next of adjacency.get(nodeId) ?? []) stack.push(next);
    }

    return false;
  };

  if (!hasPathToEnd(triggerNodes[0].id)) {
    issues.push({
      code: 'NO_PATH_TO_END',
      message: 'No reachable path from the trigger leads to an end node.',
    });
  }
};

const buildAdjacencyMap = (
  nodes: WorkflowNodeDefinition[],
  transitions: WorkflowTransitionDefinition[]
) => {
  const adjacency = new Map<string, string[]>();
  nodes.forEach((node) => adjacency.set(node.id, []));
  transitions.forEach((transition) => adjacency.get(transition.sourceNodeId)?.push(transition.targetNodeId));
  return adjacency;
};

const transitionCreatesCycle = (
  transition: WorkflowTransitionDefinition,
  adjacency: Map<string, string[]>
) => {
  if (transition.sourceNodeId === transition.targetNodeId) return true;

  const visited = new Set<string>();
  const stack = [...(adjacency.get(transition.targetNodeId) ?? [])];

  while (stack.length > 0) {
    const nodeId = stack.pop();
    if (!nodeId || visited.has(nodeId)) continue;
    if (nodeId === transition.sourceNodeId) return true;
    visited.add(nodeId);
    for (const nextNodeId of adjacency.get(nodeId) ?? []) {
      stack.push(nextNodeId);
    }
  }

  return false;
};
